import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, ArrowLeft, Eye, EyeOff, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SpeechBubble, MillieHero } from "../components/MascotBubble";

function Shell({ mood, bubble, children, testId }) {
  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-gradient-to-b from-[#FFF6F1] to-[#FCE7EC] px-6 pt-10 pb-12" data-testid={testId}>
        <Link to="/login" data-testid="reset-back" className="inline-flex items-center gap-1 text-sm font-bold text-sorted-slate"><ArrowLeft size={16} strokeWidth={2.5} /> Back to sign in</Link>
        <div className="relative h-44 mt-2">
          <MillieHero mood={mood} className="absolute right-0 bottom-0 h-44 w-[44%]" testId="reset-millie" />
          <div className="absolute left-0 top-2 max-w-[54%]"><SpeechBubble tail="br" testId="reset-bubble">{bubble}</SpeechBubble></div>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Shell mood="thinking" testId="forgot-page" bubble={sent ? "Sent! Check your inbox (and the spam folder, just in case)." : "Locked out? Happens to the best of us. Pop your email in and I'll send a reset link."}>
      <h1 className="font-hand text-4xl text-sorted-ink">Forgot password</h1>
      {sent ? (
        <div className="mt-4 bg-white rounded-3xl border-2 border-[#FDECEF] p-4 shadow-soft" data-testid="forgot-sent">
          <p className="text-sm text-sorted-ink font-semibold">If <b>{email}</b> is with us, a reset link is on its way. It works for 60 minutes.</p>
          <button onClick={() => setSent(false)} data-testid="forgot-again" className="mt-3 text-xs font-bold text-sorted-rose">Didn't get it? Send again</button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div className="relative">
            <Mail size={18} strokeWidth={2.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-sorted-slate" />
            <input data-testid="forgot-email" type="email" required placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border-2 border-[#EFE2DE] rounded-2xl pl-12 pr-4 py-4 text-sorted-ink font-medium outline-none focus:border-sorted-rose transition-colors" />
          </div>
          <button type="submit" disabled={busy} data-testid="forgot-submit" className="w-full bg-sorted-rose text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} strokeWidth={2.5} />} Send reset link
          </button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Those passwords don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw });
      setSession?.(data);
      toast.success("New password saved — you're in");
      navigate("/home");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!token) return <Shell mood="thinking" testId="reset-page" bubble="Hmm, that link's missing its key. Ask for a fresh one?"><Link to="/forgot-password" className="mt-4 inline-block font-bold text-sorted-rose" data-testid="reset-request-new">Request a new link</Link></Shell>;
  return (
    <Shell mood="happy" testId="reset-page" bubble="Pick something you'll remember — six characters or more.">
      <h1 className="font-hand text-4xl text-sorted-ink">New password</h1>
      <form onSubmit={submit} className="mt-4 space-y-3">
        {[["reset-password", pw, setPw, "New password"], ["reset-password-confirm", pw2, setPw2, "Type it again"]].map(([id, val, set, ph]) => (
          <div key={id} className="relative">
            <Lock size={18} strokeWidth={2.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-sorted-slate" />
            <input data-testid={id} type={show ? "text" : "password"} required minLength={6} placeholder={ph} value={val} onChange={(e) => set(e.target.value)}
              className="w-full bg-white border-2 border-[#EFE2DE] rounded-2xl pl-12 pr-12 py-4 text-sorted-ink font-medium outline-none focus:border-sorted-rose transition-colors" />
            <button type="button" onClick={() => setShow((v) => !v)} data-testid={`${id}-toggle`} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center text-sorted-slate">
              {show ? <EyeOff size={18} strokeWidth={2.5} /> : <Eye size={18} strokeWidth={2.5} />}
            </button>
          </div>
        ))}
        <button type="submit" disabled={busy} data-testid="reset-submit" className="w-full bg-sorted-rose text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} strokeWidth={2.5} />} Save new password
        </button>
      </form>
    </Shell>
  );
}
